import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import {
    acceptExitRequest,
    checkoutPoolship,
    recordSettlementCashout,
} from 'services/fund/settlement'

/**
 * How the member's remaining balance leaves the fund:
 *
 *  - `none`   — there is none. Straight to checkout.
 *  - `record` — the manager pays it themselves; we write it into the books
 *               first so the checkout that follows has nothing left to pay.
 *  - `pay`    — Hamyan pays it, out of «موجودی نزد همیان», during checkout.
 */
export type SettleMode = 'none' | 'record' | 'pay'

export interface ISettleAndExitPayload {
    moneyPool: string | undefined
    poolShip: number | undefined
    requestId: number
    mode: SettleMode
    /** the balance owed to the member; 0 when `mode` is `none` */
    amount: number
    /** destination account, on `pay` and on the member's own choice for `none` */
    bankAccount?: number
}

/** Where a failure happened, so the toast can say what did and did not happen. */
type Stage = 'record' | 'checkout' | 'accept'

const serverMessage = (error: any) => {
    const data = error?.response?.data
    if (data?.bank_account) return 'BANK_ACCOUNT'
    return data?.message || data?.detail || ''
}

/**
 * The checkout endpoint refuses in English («Poolship can't be checkouted
 * because of debt amount»). Managers should not read that, and they should
 * not read «خطایی رخ داد» either when the backend told us exactly what is
 * wrong — so the two refusals it can actually produce are translated, and
 * anything else falls back to the generic line.
 */
const checkoutRefusal = (detail: string) => {
    if (detail === 'BANK_ACCOUNT')
        return 'حساب مقصد قابل استفاده نیست؛ حساب دیگری انتخاب کنید یا «فقط ثبت شود» را بزنید.'
    if (detail.includes('debt amount'))
        return 'این عضو هنوز به صندوق بدهکار است؛ تا تسویهٔ بدهی، خروج ممکن نیست.'
    if (detail.includes('hamyan balance'))
        return 'موجودی نزد همیان برای این پرداخت کافی نیست. می‌توانید خودتان پرداخت کنید و «فقط ثبت شود» را بزنید.'
    return ''
}

class SettleError extends Error {
    stage: Stage
    status?: number
    /** what the backend said, when it said something readable */
    detail: string
    constructor(stage: Stage, cause: any) {
        super(stage)
        this.stage = stage
        this.status = cause?.response?.status
        this.detail = serverMessage(cause)
    }
}

/**
 * The whole of «قبول»: settle the account, close the membership, stamp the
 * request. Three calls, in this order, because each one depends on the last.
 *
 * This is what the PWA was missing. The screen used to ask the backend for the
 * member's balance and then — whatever the answer — do nothing with it, so
 * «قبول» silently did nothing at all and managers finished the job in the
 * Android app instead.
 *
 * Order matters for what a half-finished run leaves behind. Checkout before
 * accept means a failure at the last step leaves a settled member with an
 * unstamped request, which the manager can fix by pressing «قبول» again — the
 * reverse would leave an accepted request and a member still holding their
 * money in the fund.
 */
export default function useSettleAndExit(onDone: () => void) {
    const { isLoading, mutate } = useMutation({
        mutationFn: async (payload: ISettleAndExitPayload) => {
            if (payload.mode === 'record' && payload.amount > 0) {
                try {
                    await recordSettlementCashout({
                        moneyPool: payload.moneyPool,
                        poolShip: payload.poolShip,
                        amount: payload.amount,
                    })
                } catch (error) {
                    throw new SettleError('record', error)
                }
            }
            try {
                await checkoutPoolship({
                    moneyPool: payload.moneyPool,
                    poolShip: payload.poolShip,
                    // on «فقط ثبت شود» the money has already been handed over;
                    // passing the account here would send it a second time
                    bankAccount:
                        payload.mode === 'record'
                            ? undefined
                            : payload.bankAccount,
                })
            } catch (error) {
                throw new SettleError('checkout', error)
            }
            try {
                await acceptExitRequest({
                    moneyPool: payload.moneyPool,
                    id: payload.requestId,
                })
            } catch (error) {
                throw new SettleError('accept', error)
            }
        },
        onSuccess: (_, variables) => {
            posthog.capture('exit_request_settled', {
                moneypool_id: variables.moneyPool,
                mode: variables.mode,
                amount: variables.amount,
            })
            toast.success('تسویه انجام شد و عضو از صندوق خارج شد.')
            onDone()
        },
        onError: (error: any, variables) => {
            const stage: Stage = error?.stage || 'checkout'
            posthog.capture('exit_request_settle_failed', {
                moneypool_id: variables.moneyPool,
                mode: variables.mode,
                stage,
                status: error?.status,
            })
            if (stage === 'accept') {
                // the money moved and the member is out; only the stamp on the
                // request is missing, and saying «خطا» here would send the
                // manager off to settle an already-settled member
                toast.error(
                    'تسویه انجام شد، ولی وضعیت درخواست ثبت نشد. یک بار دیگر «قبول» را بزنید.'
                )
                onDone()
                return
            }
            if (stage === 'record') {
                toast.error('ثبت برداشت انجام نشد؛ چیزی تغییر نکرد.')
                return
            }
            // «فقط ثبت شود» writes the cash-out before the checkout, so a
            // failure here has already moved the books even though the member
            // is still in the fund. Saying «چیزی تغییر نکرد» would be false.
            toast.error(
                checkoutRefusal(error?.detail || '') ||
                    (variables.mode === 'record'
                        ? 'برداشت ثبت شد ولی خروج عضو انجام نشد؛ یک بار دیگر «قبول» را بزنید.'
                        : 'تسویه انجام نشد؛ چیزی تغییر نکرد.')
            )
        },
    })

    return { isSettling: isLoading, settle: mutate }
}
